import {useRef} from 'react';

/**
 * A hook that memoizes a value using a custom equality function.
 * Unlike `useMemo`, this preserves the previous value when the equality check passes,
 * providing stable references without relying on referential equality.
 *
 * @param value - The value to memoize
 * @param equals - A function that returns true if the previous and next values are considered equal
 * @returns The memoized value
 *
 * @example
 * const memoized = useCustomMemo(items, (prev, next) => prev.length === next.length);
 */
export const useCustomMemo = <T>(value: T, equals: (next: T, prev: T) => boolean) => {
    const memoValueRef = useRef(value);

    if (!equals(value, memoValueRef.current)) {
        memoValueRef.current = value;
    }

    return memoValueRef.current;
};
